import 'whatwg-fetch'
import * as Sentry from '@sentry/gatsby'
import { builder } from '@builder.io/react'
import { registerBuilderComponents } from './src/utils/Builder/componentRegistry'

import { IS_FOREIGN_COUNTRY } from './src/utils/constants'

// GATSBY_TODO — Remove when gatsby is retired.
// Env var gate: if missing or not 'true', all navigations stay on Gatsby. Safe default.
const HYDROGEN_PROXY_ENABLED = process.env.GATSBY_ENABLE_HYDROGEN_PROXY === 'true'

/**
 * Paths owned by Hydrogen — force a full page load so the edge function routes to Hydrogen.
 * Matching mirrors gatsby-proxy.ts: exact match OR prefix match with path + '/'.
 * Add paths here as hydrogen pages launch — must stay in sync with HYDROGEN_PATHS in gatsby-proxy.ts and routes.ts
 */
const HYDROGEN_EXACT_PATHS = [
  '/',
  '/en-us',
  '/frame',
  '/frame-2',
  '/calendar',
  '/calendar-max',
  '/products',
  '/cart',
  '/buddy',
  '/add-accessory',
  '/replacement-parts',
  '/tos',
  '/privacy-policy',
  '/sms-terms',
  '/sms-privacy',
  '/pages/about-us',
  '/press',
  '/refer',
  '/friends',
  '/careers',
  '/pages/retailers',
  '/skylight-affiliate-partner-program',
] // Hydrogen paths — keep in sync with HYDROGEN_PATHS in gatsby-proxy.ts
const isHydrogenPath = (pathname) =>
  HYDROGEN_PROXY_ENABLED &&
  HYDROGEN_EXACT_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`)) // handles nested routes

/**
 * Bridges Gatsby state to Hydrogen and returns a full URL for hard navigation.
 * Writes cart cookie, appends UTM and promo params from localStorage to comply with Hydrogen's data contract.
 */
const bridgeStateAndNavigateToH2 = (to) => {
  const url = new URL(to, window.location.origin)

  // Write Hydrogen's cart cookie from Gatsby's localStorage cart ID.
  // Hydrogen reads a cookie named 'cart' containing just the ID suffix (not the full GID).
  // Must be set before the reload so the server sees it on the first request.
  try {
    const cartId = localStorage.getItem('shopify_cart_id')
    if (cartId) {
      const suffix = cartId.split('/').pop()
      if (suffix) document.cookie = `cart=${encodeURIComponent(suffix)}; path=/`
    }
  } catch (e) {
    /* localStorage unavailable — ignore */
  }

  // Append UTM params from localStorage if not already in the URL
  try {
    const storedUtm = localStorage.getItem('utm_params')
    if (storedUtm) {
      const utmParams = JSON.parse(storedUtm)
      for (const [key, value] of Object.entries(utmParams)) {
        if (value && !url.searchParams.has(key)) url.searchParams.set(key, value)
      }
    }
  } catch (e) {
    /* malformed localStorage — ignore */
  }

  // Append promo params from localStorage if not already in the URL
  try {
    const storedBanner = localStorage.getItem('banner')
    if (storedBanner) {
      const { promoCode, channel, amount } = JSON.parse(storedBanner)
      if (promoCode && !url.searchParams.has('promo')) url.searchParams.set('promo', promoCode)
      if (channel && !url.searchParams.has('channel')) url.searchParams.set('channel', channel)
      if (amount && !url.searchParams.has('amount')) url.searchParams.set('amount', amount)
    }
  } catch (e) {
    /* malformed localStorage — ignore */
  }

  return url.toString()
}

export const onClientEntry = () => {
  builder.init(process.env.GATSBY_BUILDER_PUB_API_KEY)
  registerBuilderComponents()

  // GATSBY_TODO — Remove when gatsby is retired.
  // Monkey-patch Gatsby's internal navigate function to intercept Hydrogen-bound navigations.
  // ___navigate is upstream of both <Link> clicks and programmatic navigate() calls.
  // Patching here prevents the SPA transition entirely — no render cycle, no content flash.
  // Safe because Gatsby v3 is frozen and this internal will never change.
  // Production build assigns ___navigate BEFORE onClientEntry (unlike dev source suggests).
  // Capture the existing value, wrap it, then use defineProperty to guard against re-assignment.
  if (!IS_FOREIGN_COUNTRY) {
    let originalNavigate = window.___navigate
    const patchedNavigate = (to, options) => {
      const rawPath = typeof to === `string` ? to : to?.pathname ?? ``
      const pathname = rawPath.split(`?`)[0].split(`#`)[0]
      if (isHydrogenPath(pathname.replace(/\/+$/, ``) || `/`)) {
        try {
          window.location.href = bridgeStateAndNavigateToH2(
            typeof to === `string` ? to : `${to.pathname || ``}${to.search || ``}${to.hash || ``}`
          )
          return
        } catch (error) {
          Sentry.captureException(error, {
            tags: { system: 'cross-site-proxy', component: 'gatsby-navigate-patch' },
          })
          // Fall through to originalNavigate — user gets Gatsby's page instead of a dead click
        }
      }
      return originalNavigate(to, options)
    }

    window.___navigate = patchedNavigate
    Object.defineProperty(window, `___navigate`, {
      configurable: true,
      enumerable: true,
      set(fn) {
        originalNavigate = fn
      },
      get() {
        return patchedNavigate
      },
    })
  }
}

export const onRouteUpdate = ({ location }) => {
  if (IS_FOREIGN_COUNTRY) {
    const scriptId = 'trustpilot-widget-script'
    const targetStrings = ['the-skylight-frame', 'the-skylight-calendar-15', 'skylight-plus']
    // Function to load the script and initialize the widget
    const loadScript = () => {
      // Remove existing script if it exists
      const existingScript = document.getElementById(scriptId)
      if (existingScript) {
        existingScript.remove()
      }
      // Create a new script element
      const script = document.createElement('script')
      script.id = scriptId
      script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
      script.async = true
      script.onload = () => {
        // Re-initialize the Trustpilot widget after the script loads
        if (window.Trustpilot) {
          window.Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'), true)
        }
      }
      document.body.appendChild(script)
    }
    if (targetStrings.some((str) => location.pathname.includes(str))) {
      loadScript()
    }
  }
}
